import { OFFICE_URL } from '@/constants';

/**
 * Truncate string, show ellipsis when exceeding length
 * @param str Original string
 * @param length Maximum length
 * @returns Processed string
 */
export const truncateString = (str: string, length: number): string => {
  if (str.length <= length) {
    return str;
  }
  return str.substring(0, length) + '...';
};

/**
 * Capitalize first letter
 * @param str Original string
 * @returns String with first letter capitalized
 */
export const capitalizeFirstLetter = (str: string): string => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Convert camelCase to kebab-case
 * @param str Camel case string
 * @returns Kebab case string
 */
export const camelToKebab = (str: string): string => {
  return str.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
};

/**
 * Convert kebab-case to camelCase
 * @param str Kebab case string
 * @returns Camel case string
 */
export const kebabToCamel = (str: string): string => {
  return str.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
};

/**
 * Generate random string
 * @param length String length
 * @returns Random string
 */
export const generateRandomString = (length: number = 8): string => {
  const chars =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

/**
 * Validate email format
 * @param email Email address
 * @returns Whether it is a valid email format
 */
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[A-Za-z0-9._-]+@[A-Za-z0-9._-]+\.[A-Za-z0-9._-]+$/;
  return emailRegex.test(email);
};

/**
 * Validate phone number format
 * @param phone Phone number
 * @returns Whether it is a valid phone number format
 */
export const validatePhone = (phone: string): boolean => {
  const phoneRegex = /^\d{8,15}$/;
  return phoneRegex.test(phone);
};

/**
 * Validate user code format
 * @param userCode User code
 * @returns Whether it is a valid user code format
 */
export const validateUserCode = (userCode: string): boolean => {
  const userCodeRegex = /^[a-z0-9._-]{1,20}$/;
  return userCodeRegex.test(userCode);
};

/**
 * Mask phone number middle part
 * @param phone Phone number
 * @returns Masked phone number
 */
export const maskPhone = (phone: string): string => {
  if (!phone || phone.length < 10) return phone;
  return phone.replace(/(\d{3})\d{4,5}(\d{4})/, '$1****$2');
};

/**
 * Mask email part
 * @param email Email address
 * @returns Masked email
 */
export const maskEmail = (email: string): string => {
  const parts = email.split('@');
  if (parts.length !== 2) return email;

  const username = parts[0];
  const domain = parts[1];

  if (username.length <= 3) {
    return username.charAt(0) + '***@' + domain;
  }

  return username.substring(0, 3) + '***@' + domain;
};

/**
 * Get Office URL
 * @param domain Domain name
 * @returns Office URL
 */
export const getOfficeUrl = (domain: string) => {
  return OFFICE_URL.replace('{domain}', domain);
};

/**
 * Convert string to uppercase
 * @param str Original string
 * @returns Uppercase string
 */
export const uppercase = (str: string): string => {
  return str.toUpperCase();
};

/**
 * Check if string is empty
 * @param str String to check
 * @returns Whether it is empty
 */
export const isEmpty = (str: string): boolean => {
  return str === null || str === '';
};

/**
 * Check if string is not empty
 * @param str String to check
 * @returns Whether it is not empty
 */
export const isNotEmpty = (str: string): boolean => {
  return str !== '';
};

/**
 * Get first capitalized letter of string
 * @param str Original string
 * @returns First capitalized letter
 */
export const getFirstCapitalizedLetter = (str: string): string => {
  return str.charAt(0).toUpperCase();
};

// Validate URL format
export const isValidUrl = (value: string) => {
  if (!value) return true;
  const urlPattern =
    /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-.~:/?#[\]@!$&'()*+,;=%]*)?$/;
  return urlPattern.test(value);
};

// Validate IP or CIDR format
export const isValidIpOrCidr = (value: string) => {
  if (!value) return true;
  // Validate IP or CIDR format
  // 10.10.100.10 or 10.10.100.0/24
  // 10.10.100.10/32
  // 10.10.100.0/24
  // 10.10.100.0/25
  const ipPattern =
    /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)(\/([0-9]|[12][0-9]|3[0-2]))?$/;
  return ipPattern.test(value);
};

/**
 * Format phone number based on country code
 * @param countryCode Country code (e.g., '82' for Korea)
 * @param phone Phone number (digits only)
 * @returns Formatted phone number
 */
export const formatPhoneNumber = (
  countryCode: string,
  phone: string
): string => {
  if (!countryCode || !phone) return phone;
  const digits = phone.replace(/\D/g, '');

  switch (countryCode) {
    case '82':
      // Korea: 010-XXXX-XXXX or 01X-XXX-XXXX
      if (digits.length >= 10) {
        if (digits.length === 10) {
          return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
        }
        return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
      }
      break;
    case '1':
      // US/Canada: XXX-XXX-XXXX
      if (digits.length >= 10) {
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
      }
      break;
    case '81':
      // Japan: XXX-XXXX-XXXX
      if (digits.length >= 10) {
        return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
      }
      break;
    case '86':
      // China: XXX-XXXXXXX or XXXX-XXXXXXX
      if (digits.length >= 11) {
        return `${digits.slice(0, 3)}-${digits.slice(3)}`;
      }
      break;
    case '65':
      // Singapore: XXX-XXXX
      if (digits.length >= 8) {
        return `${digits.slice(0, 4)}-${digits.slice(4)}`;
      }
      break;
    default:
      // Default format
      if (digits.length >= 10) {
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
      }
  }

  return digits;
};
